<template>
  <v-container fluid>
    <v-dialog v-model="dialogForm.show" persistent :overlay="false" max-width="600px">
      <v-card>
        <v-form ref="schedulingForm" lazy-validation>
          <v-card-title class="title primary white--text mb-4">{{dialogForm.event && dialogForm.event.id ? 'Edit' : 'Add'}} Shifts</v-card-title>
          <v-card-text>
            <p>
              Please provide the appropriate details below.
            </p>

            <v-autocomplete
              placeholder="Crews"
              :items="selectedJobSiteCrews"
              item-value="id"
              item-text="name"
              v-model="dialogForm.event.crewId"
              filled
              class="mb-7"
              @change="updateDialogEmpoyees"
              hint="Selecting a crew will auto-select the employees in the next field."
              persistent-hint
              v-if="selectedJobSite"
            >
            </v-autocomplete>

            <v-autocomplete
              placeholder="Employee Search"
              :items="allEmployees"
              item-value="docId"
              item-text="name"
              v-model="dialogForm.event.employees"
              filled
              hide-details
              class="mb-7"
              :rules="[dialogForm.rules.requiredArray]"
              multiple
            >
            </v-autocomplete>

            <v-autocomplete
              placeholder="Notify User Search"
              :items="notificationUsers"
              item-value="id"
              item-text="name"
              v-model="dialogForm.event.notifyUserId"
              filled
              class="mb-7"
              hint="Selecting a user will email them this shift information upon each save."
              persistent-hint
            >
            </v-autocomplete>

            <v-menu
              ref="firstShiftDateMenu"
              v-model="dialogForm.menu1"
              :close-on-content-click="false"
              :return-value.sync="date"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="dialogForm.event.firstShiftDate"
                  label="First Shift Date"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  filled
                  :rules="[dialogForm.rules.required]"
                  required
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="dialogForm.event.firstShiftDate"
                no-title
                scrollable
              >
                <v-spacer></v-spacer>
                <v-btn
                  color="primary"
                  @click="$refs.firstShiftDateMenu.save(date)"
                >
                  OK
                </v-btn>
              </v-date-picker>
            </v-menu>

            <v-menu
              ref="lastShiftDateMenu"
              v-model="dialogForm.menu2"
              :close-on-content-click="false"
              :return-value.sync="date"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="dialogForm.event.lastShiftDate"
                  label="Last Shift Date"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  filled
                  :rules="[dialogForm.rules.required, isGreaterThanFirstShiftDate]"
                  required
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="dialogForm.event.lastShiftDate"
                no-title
                scrollable
              >
                <v-spacer></v-spacer>
                <v-btn
                  color="primary"
                  @click="$refs.lastShiftDateMenu.save(date)"
                >
                  OK
                </v-btn>
              </v-date-picker>
            </v-menu>

            <v-text-field
              label="Shift Start Time"
              v-model="dialogForm.event.startTime"
              type="time"
              filled
              :rules="[dialogForm.rules.required]"
              required
            ></v-text-field>

            <v-text-field
              label="Shift End Time"
              v-model="dialogForm.event.endTime"
              type="time"
              filled
              :rules="[dialogForm.rules.required]"
              required
            ></v-text-field>

            <v-select
              v-model="dialogForm.event.daysOfWeek"
              :items="daysOfWeek"
              label="Days of Week"
              multiple
              filled
              :rules="[requiredDaysOfWeek]"
              v-show="showDaysOfWeekSelector"
            ></v-select>

          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              text
              @click="cancelDialogForm"
              >Cancel</v-btn
            >
            <v-btn
              color="primary"
              @click="saveSchedule"
              :disabled="dialogForm.saving"
              :loading="dialogForm.saving"
              >
              Save
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>
    <h1 class="headline mb-4 pt-4">
      Employee Schedule
    </h1>
    <job-selector
      @jobSiteChange="onJobSiteChange"
      :noAsbestosFilter="true"
      showHint="Filter calendar to a specific job site."
      class="mb-5"
    />
    <v-row wrap>
      <v-col cols="12" xs="12">
        <v-card class="elevation-0">
          <v-card-title
            class="pa-3 primary white--text font-weight-black headline"
          >
            <span class="ml-4">{{calendarName}}</span>
            <v-spacer></v-spacer>
            <v-btn
              small
              class="ml-2"
              color="light"
              :to="`/employee-schedule/crews`"
            >
              Manage Crews
            </v-btn>
          </v-card-title>
          <v-card-text class="pt-4 px-1">
            <v-container fluid>
              <v-row class="fill-height">
                <v-col>
                  <v-sheet height="64">
                    <v-toolbar
                      flat
                    >
                      <v-btn
                        outlined
                        class="mr-4"
                        color="grey darken-2"
                        @click="setToday"
                      >
                        Today
                      </v-btn>
                      <v-select
                        v-model="type"
                        :items="types"
                        dense
                        outlined
                        hide-details
                        class="ma-2"
                        style="max-width: 125px;"
                        label="Calendar"
                      ></v-select>
                      <v-select
                        v-model="filters.scheduledEmployee"
                        :items="scheduledEmployeeOptions"
                        dense
                        outlined
                        hide-details
                        class="ma-2"
                        style="max-width: 225px;"
                        label="Scheduled Employees"
                        clearable
                      ></v-select>
                      <v-select
                        v-model="filters.departmentName"
                        :items="departmentOptions"
                        dense
                        outlined
                        hide-details
                        class="ma-2"
                        style="max-width: 225px;"
                        label="Job Site Department"
                        clearable
                      ></v-select>
                      <v-btn
                        fab
                        text
                        small
                        color="grey darken-2"
                        @click="prev"
                      >
                        <v-icon small>
                          mdi-chevron-left
                        </v-icon>
                      </v-btn>
                      <v-btn
                        fab
                        text
                        small
                        color="grey darken-2"
                        @click="next"
                      >
                        <v-icon small>
                          mdi-chevron-right
                        </v-icon>
                      </v-btn>
                      <v-toolbar-title v-if="$refs.calendar">
                        {{ $refs.calendar.title }}
                      </v-toolbar-title>
                      <v-spacer></v-spacer>
                      <v-btn
                        color="primary"
                        @click="addShifts"
                      >
                        <span>Add Shifts</span>
                      </v-btn>
                    </v-toolbar>
                  </v-sheet>
                  <v-sheet height="600" style="position: relative;">
                    <v-overlay absolute light color="white" opacity="0.7" :value="showLoading">
                      <div class="text-center">
                        <v-progress-circular
                          indeterminate
                          color="primary"
                          size="48"
                        ></v-progress-circular>
                        <div class="mt-3 grey--text text--darken-2">Loading schedules...</div>
                      </div>
                    </v-overlay>
                    <v-calendar
                      ref="calendar"
                      v-model="focus"
                      color="primary"
                      :events="filteredEvents"
                      :type="type"
                      @click:event="showEvent"
                      @click:more="viewDay"
                      @click:date="viewDay"
                      @change="updateRange"
                    >
                      <template #event="event">
                        &nbsp;{{ event.eventParsed.input.name }}
                      </template>
                    </v-calendar>
                  </v-sheet>
                  <v-menu
                    v-model="selectedOpen"
                    :close-on-content-click="false"
                    :activator="selectedElement"
                    offset-x
                    min-width="350px"
                    max-width="450px"
                  >
                    <v-card
                      flat
                    >
                      <v-toolbar
                        color="primary"
                        dark
                      >
                        <v-toolbar-title>{{selectedEvent.name}}</v-toolbar-title>
                        <v-spacer></v-spacer>
                        <v-btn @click="selectedOpen = false" icon>
                          <v-icon>mdi-close</v-icon>
                        </v-btn>
                      </v-toolbar>
                      <v-card-text v-if="selectedEvent.record">
                        <div><strong>Job Site: </strong> {{(selectedEvent.jobSiteRecord || {}).street}}</div>
                        <div><strong>First Shift Date: </strong> {{selectedEvent.firstShiftDate | moment("M/D/YYYY")}}</div>
                        <div><strong>Last Shift Date: </strong> {{selectedEvent.lastShiftDate | moment("M/D/YYYY")}}</div>
                        <div><strong>Shift Start Time: </strong> {{this.formatTime(selectedEvent.start)}}</div>
                        <div><strong>Shift End Time: </strong> {{this.formatTime(selectedEvent.end)}}</div>
                        <div><strong>Shift Days: </strong>
                          <span class="day-of-week" :class="{ 'active' : (selectedEvent.daysOfWeek || []).includes('Sunday')}">S</span>
                          <span class="day-of-week" :class="{ 'active' : (selectedEvent.daysOfWeek || []).includes('Monday')}">M</span>
                          <span class="day-of-week" :class="{ 'active' : (selectedEvent.daysOfWeek || []).includes('Tuesday')}">T</span>
                          <span class="day-of-week" :class="{ 'active' : (selectedEvent.daysOfWeek || []).includes('Wednesday')}">W</span>
                          <span class="day-of-week" :class="{ 'active' : (selectedEvent.daysOfWeek || []).includes('Thursday')}">T</span>
                          <span class="day-of-week" :class="{ 'active' : (selectedEvent.daysOfWeek || []).includes('Friday')}">F</span>
                          <span class="day-of-week" :class="{ 'active' : (selectedEvent.daysOfWeek || []).includes('Saturday')}">S</span>
                        </div>
                        <div><strong>Employees: </strong> {{selectedEvent.employeeNames.join(', ')}}</div>
                      </v-card-text>
                      <v-card-actions>
                        <v-btn
                          text
                          outlined
                          color="primary"
                          @click="editScheduling"
                        >
                          Edit
                        </v-btn>
                        <v-btn
                          text
                          outlined
                          color="red"
                          :disabled="saving"
                          :loading="saving"
                          @click="deleteSchedule"
                        >
                          Delete
                        </v-btn>
                      </v-card-actions>
                    </v-card>
                  </v-menu>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-snackbar v-model="snack" :timeout="3000" :color="snackColor">
      {{ snackText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snack = false"> Close </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
 import fb from "@/main";
 import moment from "moment";
 import { mapState } from "vuex";
 import JobSelector from "@/components/JobSelector";
 import { cloneDeep, uniq, sortBy, flatMap, truncate, compact, keyBy} from "lodash";

 const daysArray = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

 const seededRandom = (seed) => {
   var x = Math.sin(seed) * 10000;
   return x - Math.floor(x);
 }
 const generateColorHex = (seed) => {
   var random = seededRandom(seed * 555);
   var color = Math.floor(random * 16777215).toString(16);
   return "#" + color.padStart(6, '0');
 }

 export default {
    components: {
      JobSelector,
    },
    data: () => ({
      today: '',
      focus: '',
      type: 'month',
      types: ['month', 'week', 'day'],
      selectedEvent: {},
      selectedElement: null,
      selectedOpen: false,
      date: '',
      loading: false,
      saving: false,
      filters: {
        scheduledEmployee: "",
        departmentName: ""
      },
      dialogForm: {
        show: false,
        saving: false,
        menu1: false,
        menu2: false,
        rules: {
          required: value => !!value || 'Required.',
          requiredArray: value => !!value && !!value.length || 'Required.'
        },
        event: {
          daysOfWeek: daysArray
        }
      },
      snack: false,
      snackColor: "",
      snackText: "",
      records: [], // employeeSchedule docs, frozen so Vue doesn't make them reactive
      range: null, // visible calendar dates (padded), { start, end } as YYYY-MM-DD
      loadedFrom: null, // lastShiftDate cutoff of the current all job sites query
      fetchId: 0,
      extraJobSites: {}, // closed job sites referenced by schedules, fetched by id
      daysOfWeek: daysArray,
      selectedJobSite: null,
      crews: [],
      notificationUsers: []
    }),
    mounted () {
      this.$refs.calendar.move(0);
    },
    async created() {
      if (!this.employees.length) {
        await this.$store.dispatch("getEmployees", fb);
      }
      this.getCrews();
      this.getNotificationUsers();
    },
    watch: {
      jobSites () {
        this.resolveMissingJobSites(this.records);
      }
    },
    computed: {
      ...mapState({
        user: "user",
        employees: "employees",
        jobSites: "jobSites",
      }),
      selectedJobSiteId () {
        return this.selectedJobSite && this.selectedJobSite.id;
      },
      // also wait on the employees, job sites, and any closed job sites being fetched, event names come from them
      showLoading () {
        return this.loading || !this.employees.length || !this.jobSites.length || this.records.some((r) => r.jobSite && !(r.jobSite in this.jobSitesById));
      },
      calendarName () {
        return this.selectedJobSiteId ? `${this.selectedJobSite.street}` : `All Job Sites`;
      },
      jobSitesById () {
        return { ...this.extraJobSites, ...keyBy(this.jobSites, 'id') };
      },
      employeeNamesById () {
        const names = {};
        this.allEmployees.forEach((e) => { names[e.docId] = e.name; });
        return names;
      },
      selectedJobSiteCrews () {
        if (!this.selectedJobSiteId) {
          return [];
        }
        // crews without a jobSite value set should be shown for all job sites
        return this.crews.filter((c) => c.jobSite === this.selectedJobSiteId).concat(this.crews.filter((c) => !c.jobSite));
      },
      // one calendar event per shift day, only for the visible date range
      events () {
        if (!this.range) {
          return [];
        }
        const { start, end } = this.range;
        const events = [];
        this.records.forEach((record) => {
          if (record.lastShiftDate < start || record.firstShiftDate > end) {
            return;
          }
          const daysOfWeek = record.daysOfWeek.map((d) => {return d.toLowerCase()});
          const employeeNames = record.employees.map((empId) => {
            return (this.employeeNamesById[empId] || "").trim();
          }).sort();
          const jobSiteRecord = this.jobSitesById[record.jobSite];
          const startEndTime = `${this.formatShiftTime(record.startTime)} - ${this.formatShiftTime(record.endTime)}`;
          let name = startEndTime;
          let colorHex = generateColorHex(name.replace(/\D/g,''));
          if (!this.selectedJobSiteId && jobSiteRecord) {
            name = `${truncate(jobSiteRecord.street, {'length': 14,'omission': '...'})} | ${name} `;
            colorHex = generateColorHex(jobSiteRecord.street.replace(/\D/g,''));
          }
          const [startHours, startMinutes] = record.startTime.split(':').map(Number);
          const [endHours, endMinutes] = record.endTime.split(':').map(Number);

          const currentDate = moment.max(moment(record.firstShiftDate), moment(start));
          const endDate = moment.min(moment(record.lastShiftDate), moment(end));
          while (currentDate <= endDate) {
            if (daysOfWeek.includes(currentDate.format('dddd').toLowerCase())) {
              const startDateTime = currentDate.clone().set({hour:startHours,minute:startMinutes,second:0,millisecond:0}).valueOf();
              const endDateTime = currentDate.clone().set({hour:endHours,minute:endMinutes,second:0,millisecond:0}).valueOf();
              events.push({
                start: startDateTime,
                end: endDateTime < startDateTime ? endDateTime + 86400000 : endDateTime,
                timed: true,
                name: name, // calendar item preview text
                startEndTime: startEndTime,
                color: colorHex,
                record: record,
                firstShiftDate: record.firstShiftDate,
                lastShiftDate: record.lastShiftDate,
                daysOfWeek: record.daysOfWeek,
                employeeNames: employeeNames,
                jobSiteRecord: jobSiteRecord
              });
            }
            // calendar days, not 24 hours, so the DST change doesn't skip or repeat a day
            currentDate.add(1, 'day');
          }
        });
        return events;
      },
      filteredEvents () {
        let list = this.events;
        if (this.filters.scheduledEmployee) {
          list = list.filter((ev) => {return ev.employeeNames.length && ev.employeeNames.includes(this.filters.scheduledEmployee);});
        }
        if (this.filters.departmentName) {
          list = list.filter((ev) => {return ev.jobSiteRecord && ev.jobSiteRecord.department.includes(this.filters.departmentName);});
        }
        return list;
      },
      // built from every loaded schedule, not just the visible dates, so a selection stays in the list
      scheduledEmployeeOptions () {
        return sortBy(compact(uniq(flatMap(this.records, (r) => {return r.employees.map((empId) => (this.employeeNamesById[empId] || "").trim())}))));
      },
      departmentOptions () {
        return sortBy(compact(uniq(flatMap(this.records, (r) => {return (this.jobSitesById[r.jobSite] || {}).department}))));
      },
      allEmployees () {
        return this.employees.map((e) => {
          return {
            name: `${e.firstName} ${e.lastName} (${e.barcodeId})`,
            ...e,
          };
        });
      },
      showDaysOfWeekSelector () {
        let daysOfSchedule = 0;
        if (this.dialogForm.event.firstShiftDate && this.dialogForm.event.lastShiftDate) {
          daysOfSchedule = moment(this.dialogForm.event.lastShiftDate).diff(moment(this.dialogForm.event.firstShiftDate), 'days') + 1;
        }
        return daysOfSchedule > 2;
      }
    },
    methods: {
      onJobSiteChange (jobSite) {
        this.selectedJobSite = jobSite;
        this.records = [];
        this.loadedFrom = null;
        this.getSchedules();
      },
      updateRange ({ start, end }) {
        // pad a week on each side: month view also shows days from the neighboring months,
        // and overnight shifts from the day before the range spill into it
        this.range = {
          start: moment(start.date).subtract(7, 'days').format('YYYY-MM-DD'),
          end: moment(end.date).add(7, 'days').format('YYYY-MM-DD'),
        };
        // the all job sites query has no end date, so moving forward reuses what's loaded
        if (!this.selectedJobSiteId && (!this.loadedFrom || this.range.start < this.loadedFrom)) {
          this.getSchedules();
        }
      },
      setToday () {
        this.focus = ''
      },
      viewDay ({ date }) {
        this.focus = date
        this.type = 'day'
      },
      prev () {
        this.$refs.calendar.prev()
      },
      next () {
        this.$refs.calendar.next()
      },
      showEvent ({ nativeEvent, event }) {
        const open = () => {
          this.selectedEvent = event
          this.selectedElement = nativeEvent.target
          requestAnimationFrame(() => requestAnimationFrame(() => this.selectedOpen = true))
        }

        if (this.selectedOpen) {
          this.selectedOpen = false
          requestAnimationFrame(() => requestAnimationFrame(() => open()))
        } else {
          open()
        }

        nativeEvent.stopPropagation()
      },
      getDaysOverlapInRange (startDate, endDate, selectedDays) {
        // find 
        let start = moment(startDate);
        let end = moment(endDate);
        let daysOfRange = [];
        while (start <= end) {
            daysOfRange.push(start.format('dddd'));
            start.add(24, 'hours');
        }
        const overlap = [];
        for (let day of selectedDays) {
            if (daysOfRange.includes(day)) {
              overlap.push(day)
            }
        }
        return uniq(overlap);
      },
      requiredDaysOfWeek (value) {
        // check that the user selected days that actually exist in the date range
        const daysOverlap = this.getDaysOverlapInRange(
          this.dialogForm.event.firstShiftDate,
          this.dialogForm.event.lastShiftDate,
          this.dialogForm.event.daysOfWeek
        )
        if (this.showDaysOfWeekSelector) {
          if (!value || !value.length > 0) {
            return "Required.";
          }
          if (!daysOverlap.length) {
            return "Please select days of week that exist in date range.";
          }
        }
        return true;
      },
      isGreaterThanFirstShiftDate (value) {
        return !value || !this.dialogForm.event.firstShiftDate || new Date(value) >= new Date(this.dialogForm.event.firstShiftDate) || 'Last shift date must be equal or greater than first shift date.';
      },
      async updateDialogEmpoyees() {
        if (this.selectedJobSiteCrews.length) {
          const crew = this.selectedJobSiteCrews.find((c) => {return c.id === this.dialogForm.event.crewId});
          const employees = crew ? crew.employees : [];
          this.dialogForm.event.employees = cloneDeep(employees);
        }
      },
      async getCrews() {
        const querySnapshot = await fb.db.collection("employeeScheduleCrews").get();
        this.crews = querySnapshot.docs.map(doc => {return {id: doc.id, ...doc.data()}});
      },
      async getNotificationUsers() {
        const usersRes = await fb.db.collection("users").where("role", "in", ["Foreman", "Project Manager"]).get();
        const data = usersRes.docs.map(doc => {return {id: doc.id, ...doc.data()}});
        data.forEach((d) => d.name = `${d.firstName} ${d.lastName}`)
        this.notificationUsers = data.filter((u) => u.access);
      },
      async getSchedules() {
        const selectedJobSiteId = this.selectedJobSiteId;
        if (!selectedJobSiteId && !this.range) {
          return; // the calendar's change event hasn't reported its dates yet
        }
        const fetchId = ++this.fetchId;
        try {
          this.loading = true;
          let query = fb.db.collection("employeeSchedule");
          let loadedFrom = null;
          if (selectedJobSiteId) {
            query = query.where("jobSite", "==", selectedJobSiteId);
          } else {
            // shifts that ended before the visible dates can't show up, so skip the old history.
            // never move the cutoff later, so reloading after a save doesn't drop what's loaded
            loadedFrom = this.loadedFrom && this.loadedFrom < this.range.start ? this.loadedFrom : this.range.start;
            query = query.where("lastShiftDate", ">=", loadedFrom);
          }
          const querySnapshot = await query.get();
          if (fetchId !== this.fetchId) {
            return; // the job site or dates changed while this was loading
          }
          const records = querySnapshot.docs.map(doc => {return {id: doc.id, ...doc.data()}});
          this.records = Object.freeze(records);
          this.loadedFrom = loadedFrom;
          this.resolveMissingJobSites(records);
        } catch (error) {
          console.log(error)
        }
        if (fetchId === this.fetchId) {
          this.loading = false;
        }
      },
      // the job sites store only has open job sites, so fetch any closed ones schedules point to
      async resolveMissingJobSites(records) {
        if (!this.jobSites.length) {
          return; // store still loading, the jobSites watcher calls this again
        }
        const missingIds = uniq(records.map((r) => r.jobSite)).filter((id) => id && !(id in this.jobSitesById));
        if (!missingIds.length) {
          return;
        }
        const fetched = {};
        try {
          const docs = await Promise.all(missingIds.map((id) => fb.db.collection("jobSites").doc(id).get()));
          docs.forEach((doc) => {
            fetched[doc.id] = doc.exists ? {id: doc.id, ...doc.data()} : null;
          });
        } catch (error) {
          console.log(error)
          missingIds.forEach((id) => { fetched[id] = null; }); // show those shifts without a street rather than loading forever
        }
        this.extraJobSites = { ...this.extraJobSites, ...fetched };
      },
      // the same schedules the conflict check has always used: the selected job site's,
      // or every job site's when viewing all job sites
      async getConflictCandidates() {
        if (this.selectedJobSiteId) {
          return this.records;
        }
        // only shifts ending on or after the first proposed shift date can overlap it
        const querySnapshot = await fb.db.collection("employeeSchedule").where("lastShiftDate", ">=", this.dialogForm.event.firstShiftDate).get();
        const records = querySnapshot.docs.map(doc => {return {id: doc.id, ...doc.data()}});
        await this.resolveMissingJobSites(records);
        return sortBy(records, 'id'); // doc id order, like the unfiltered query, so the first conflict found is the same
      },
      formatShiftTime(time) {
        return moment(time, "HH:mm").format("hh:mm A");
      },
      cancelDialogForm() {
        this.dialogForm.show = false;
        this.dialogForm.event = {
          daysOfWeek: daysArray
        };
      },
      formatTime(time) {
        if (time) {
          return moment(new Date(time)).format("hh:mm A");
        } else {
          return time;
        }
      },
      async deleteSchedule() {
        if (confirm(`Are you sure? This will delete shifts for ${this.selectedEvent.record.employees.length} employees.`) == true) {
          this.saving = true;
          await fb.db.collection("employeeSchedule").doc(this.selectedEvent.record.id).delete();

          // start audit history
          const obj = {};
          ["daysOfWeek", "endTime", "firstShiftDate", "startTime", "lastShiftDate", "employees", "jobSite", "notifyUserId"].forEach((key) => {
            obj[key] = this.selectedEvent.record[key] || '';
          });
          obj.lastUpdated = new Date();
          obj.lastUpdatedBy = this.user.uid;
          obj.referenceId = this.selectedEvent.record.id;
          obj.action = "deleted";
          await fb.db.collection("employeeScheduleAuditHistory").add(obj);
          // end audit history

          this.getSchedules();
          this.selectedOpen = false;
          this.saving = false;
          this.snack = true;
          this.snackColor = "success";
          this.snackText = "Successfully deleted schedule.";
        }
      },
      doesShiftOverlap(employeeId, proposedShift, scheduledShifts) {
        const timeToMinutes = function(time) {
          const [hours, minutes] = time.split(':').map(Number);
          return hours * 60 + minutes;
        }

        // checking for overlap based on whether the shift spans to the next day:
        const checkTimeOverlap = function(startA, endA, startB, endB) {
          if (startA <= endA) { // Shift A does not span to the next day
            if (startB <= endB) { // Shift B does not span to the next day
              return endA > startB && startA < endB;
            } else { // Shift B spans to the next day
              return endA > startB || startA < (endB + 1440); // 1440 minutes in a day
            }
          } else { // Shift A spans to the next day
            if (startB <= endB) { // Shift B does not span to the next day
              return (endA + 1440) > startB && startA < endB;
            } else { // Both Shift A and B span to the next day
              return true; // Overlapping by definition, refine if necessary
            }
          }
        }

        // Convert string dates to Date objects for comparison
        const proposedStart = new Date(proposedShift.firstShiftDate);
        const proposedEnd = new Date(proposedShift.lastShiftDate);

        for (let shift of scheduledShifts) {
          const shiftStart = new Date(shift.firstShiftDate);
          const shiftEnd = new Date(shift.lastShiftDate);

          if (!(proposedEnd < shiftStart || proposedStart > shiftEnd)) {
            const dayOverlap = proposedShift.daysOfWeek.some(day => shift.daysOfWeek.includes(day));
            if (dayOverlap) {
              const proposedStartTime = timeToMinutes(proposedShift.startTime);
              const proposedEndTime = timeToMinutes(proposedShift.endTime);
              const shiftStartTime = timeToMinutes(shift.startTime);
              const shiftEndTime = timeToMinutes(shift.endTime);

              if (checkTimeOverlap(proposedStartTime, proposedEndTime, shiftStartTime, shiftEndTime) && shift.employees.includes(employeeId)) {
                return shift; // Time and day overlap found
              }
            }
          }
        }
        return false; // No overlap found
      },
      async saveSchedule() {
        // start - remove formerly active employee mainly on shifts being updated
        const activeEmployeeIds = this.employees.map((em) => em.docId)
        const selectedEmployeeIds = this.dialogForm.event.employees || [];
        this.dialogForm.event.employees = selectedEmployeeIds.filter((emId) => activeEmployeeIds.includes(emId));
        // end - remove formerly active employee mainly on shifts being updated

        const formValid = this.$refs.schedulingForm.validate();
        if (!formValid) {
          alert("Please fill out the required fields.")
          return;
        }
        if (!this.dialogForm.event.employees.length) {
          alert("Please select employees.")
          return;
        }

        const daysOfSchedule = moment(this.dialogForm.event.lastShiftDate).diff(moment(this.dialogForm.event.firstShiftDate), 'days') + 1;
        if (daysOfSchedule < 3) {
          this.dialogForm.event.daysOfWeek = uniq([
            moment(this.dialogForm.event.lastShiftDate).format('dddd'),
            moment(this.dialogForm.event.firstShiftDate).format('dddd')
          ]);
        }
        const daysOverlap = this.getDaysOverlapInRange(
          this.dialogForm.event.firstShiftDate,
          this.dialogForm.event.lastShiftDate,
          this.dialogForm.event.daysOfWeek
        )
        this.dialogForm.event.daysOfWeek = daysOverlap;


        this.dialogForm.saving = true;
        const scheduledRecords = (await this.getConflictCandidates()).filter((r) => {return r.id !== this.dialogForm.event.id}); // if editing shift then exclude it
        const schedulingConflicts = this.dialogForm.event.employees.map((employeeId) => {
          const conflict = this.doesShiftOverlap(employeeId, this.dialogForm.event, scheduledRecords);
          const employeeName = (this.employeeNamesById[employeeId] || "").trim();
          return conflict ? `${employeeName} has a scheduling conflict ${this.formatShiftTime(conflict.startTime)} - ${this.formatShiftTime(conflict.endTime)} at ${(this.jobSitesById[conflict.jobSite] || {}).street}.` : '';
        }).filter((msg) => msg);

        if (schedulingConflicts.length) {
          this.dialogForm.saving = false;
          alert(`Scheduling Conflict: ${schedulingConflicts.join('. ')}`)
          return;
        }

        if (this.dialogForm.event.id) {
          const obj = {};
          ["daysOfWeek", "endTime", "firstShiftDate", "startTime", "lastShiftDate", "employees", "jobSite", "notifyUserId"].forEach((key) => {
            obj[key] = this.dialogForm.event[key] || '';
          });
          obj.lastUpdated = new Date();
          obj.lastUpdatedBy = this.user.uid;
          await fb.db.collection("employeeSchedule").doc(this.dialogForm.event.id).update(obj);

          // start audit history
          obj.referenceId = this.dialogForm.event.id;
          obj.action = "updated";
          await fb.db.collection("employeeScheduleAuditHistory").add(obj);
          // end audit history
        } else {
          const obj = this.dialogForm.event;
          obj.createDate = new Date();
          obj.createdBy = this.user.uid;
          obj.jobSite = this.selectedJobSite.id;
          const addRes = await fb.db.collection("employeeSchedule").add(obj);

          // start audit history
          obj.referenceId = addRes.id;
          obj.action = "added";
          await fb.db.collection("employeeScheduleAuditHistory").add(obj);
          // end audit history
        }

        this.filters.scheduledEmployee = "";
        this.getSchedules();
        this.dialogForm.saving = false;
        this.dialogForm.show = false;
        this.dialogForm.event = {
          daysOfWeek: daysArray
        };
        this.snack = true;
        this.snackColor = "success";
        this.snackText = "Successfully saved schedule.";
      },
      editScheduling() {
        this.dialogForm.event = cloneDeep(this.selectedEvent.record);
        this.dialogForm.show = true;
      },
      addShifts() {
        if (this.selectedJobSite && this.selectedJobSite.id) {
          this.dialogForm.show = true
        } else {
          alert("You must select a specific job site to add shifts.")
        }
      }
    }
  }
</script>

<style scoped>
  .day-of-week {
    border: 1px solid #d4d4d4;
    color: #d4d4d4;
    padding: 1px 5px;
    border-radius: 3px;
    margin-right: 3px;
  }
  .day-of-week.active {
    border-color: rgba(0, 0, 0, 0.87);
    color: rgba(0, 0, 0, 0.87);
  }
</style>
